import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Plus, Calculator, Package, TrendingUp, TrendingDown,
    Filter, PieChart, CheckCircle2, Clock, AlertCircle,
    BarChart3, ArrowUpRight,
} from 'lucide-react';
import { Button, Card, CardStats, ConfirmModal } from '@components/ui';
import {
    getMaterialsByConstructionId,
    getMaterialCostStats,
    calculateEstimatedCost,
    calculateActualCost,
    MATERIAL_CATEGORIES,
    UNIT_TYPES,
} from '@mocks/malzemeler';
import MalzemeModal from './MalzemeModal';
import MalzemeKarti from './MalzemeKarti';
import MaliyetOzeti from './MaliyetOzeti';
import clsx from 'clsx';

export default function CostEstimation() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showSummary, setShowSummary] = useState(false);
    const [sortBy, setSortBy] = useState('cost-desc'); // cost-desc, cost-asc, name, progress

    const materials = getMaterialsByConstructionId(constructionId);
    const stats = getMaterialCostStats(constructionId);

    const filteredMaterials = useMemo(() => {
        let result = categoryFilter === 'all'
            ? materials
            : materials.filter(m => m.category === categoryFilter);

        // Sıralama
        result = [...result].sort((a, b) => {
            switch (sortBy) {
                case 'cost-desc': return calculateEstimatedCost(b) - calculateEstimatedCost(a);
                case 'cost-asc': return calculateEstimatedCost(a) - calculateEstimatedCost(b);
                case 'name': return a.name.localeCompare(b.name, 'tr');
                case 'progress': {
                    const progA = a.actualQuantity ? (a.actualQuantity / a.estimatedQuantity) : 0;
                    const progB = b.actualQuantity ? (b.actualQuantity / b.estimatedQuantity) : 0;
                    return progB - progA;
                }
                default: return 0;
            }
        });
        return result;
    }, [materials, categoryFilter, sortBy]);

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

    const formatCompact = (amount) => {
        if (Math.abs(amount) >= 1000000) return `${(amount / 1000000).toFixed(1)}M ₺`;
        if (Math.abs(amount) >= 1000) return `${Math.round(amount / 1000)}K ₺`;
        return formatCurrency(amount);
    };

    const handleAdd = () => { setSelectedMaterial(null); setModalOpen(true); };
    const handleEdit = (material) => { setSelectedMaterial(material); setModalOpen(true); };
    const handleDelete = (material) => { setSelectedMaterial(material); setDeleteModalOpen(true); };
    const confirmDelete = () => { toast.success(`${selectedMaterial?.name} başarıyla silindi`); setDeleteModalOpen(false); setSelectedMaterial(null); };
    const handleSubmit = (data) => {
        toast.success(selectedMaterial ? `${data.name} başarıyla güncellendi` : `${data.name} başarıyla eklendi`);
        setModalOpen(false);
    };

    // Kategori bazlı gruplama
    const groupedMaterials = useMemo(() => {
        const groups = {};
        filteredMaterials.forEach(m => {
            if (!groups[m.category]) groups[m.category] = [];
            groups[m.category].push(m);
        });
        return groups;
    }, [filteredMaterials]);

    // Bütçe progress
    const budgetProgress = stats.totalEstimatedCost > 0
        ? Math.min(100, (stats.totalActualCost / stats.totalEstimatedCost) * 100)
        : 0;

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-text-primary">Maliyet Tahmini</h1>
                        <p className="text-text-tertiary mt-1 text-sm">
                            {showAllConstructions ? 'Tüm inşaatlar' : selectedConstruction?.name} • Malzeme & maliyet takibi
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="soft-primary" icon={<PieChart size={18} />} onClick={() => setShowSummary(true)} className="flex-1 sm:flex-none min-h-[44px]">
                            <span className="hidden sm:inline">Maliyet Özeti</span><span className="sm:hidden">Özet</span>
                        </Button>
                        <Button variant="primary" icon={<Plus size={18} />} onClick={handleAdd} className="flex-1 sm:flex-none min-h-[44px]">
                            <span className="hidden sm:inline">Malzeme Ekle</span><span className="sm:hidden">Ekle</span>
                        </Button>
                    </div>
                </div>

                {/* Filtre + Sıralama */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex flex-wrap items-center gap-2 flex-1">
                        <Filter size={16} className="text-text-tertiary hidden sm:block" />
                        <div className="flex flex-wrap gap-1.5">
                            <button onClick={() => setCategoryFilter('all')}
                                className={clsx("px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                                    categoryFilter === 'all' ? 'bg-primary text-white' : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover')}>
                                Tümü ({materials.length})
                            </button>
                            {Object.entries(MATERIAL_CATEGORIES).map(([key, cat]) => {
                                const count = materials.filter(m => m.category === key).length;
                                if (count === 0) return null;
                                return (
                                    <button key={key} onClick={() => setCategoryFilter(key)}
                                        className={clsx("px-3 py-1.5 text-xs font-medium rounded-lg transition-colors",
                                            categoryFilter === key ? 'bg-primary text-white' : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover')}>
                                        {cat.label} ({count})
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                        className="appearance-none px-3 py-1.5 text-xs bg-bg-tertiary border border-border-light rounded-lg text-text-secondary focus:outline-none focus:ring-2 focus:ring-primary/30">
                        <option value="cost-desc">En Yüksek Maliyet</option>
                        <option value="cost-asc">En Düşük Maliyet</option>
                        <option value="name">İsme Göre (A-Z)</option>
                        <option value="progress">İlerlemeye Göre</option>
                    </select>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <CardStats title="Toplam Kalem" value={stats.totalMaterials} icon={Package} color="primary" size="sm" />
                <CardStats title="Kategori" value={stats.categories} icon={Filter} color="tertiary" size="sm" />
                <CardStats title="Tahmini Maliyet" value={formatCompact(stats.totalEstimatedCost)} icon={Calculator} color="warning" size="sm" className="col-span-1" />
                <CardStats title="Gerçekleşen" value={formatCompact(stats.totalActualCost)} icon={TrendingUp} color="success" size="sm" className="col-span-1" />
            </div>

            {/* Bütçe İlerleme Barı */}
            <Card padding="md">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
                    <div>
                        <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                            <BarChart3 size={16} className="text-text-muted" />
                            Bütçe Kullanımı
                        </h3>
                        <p className="text-xs text-text-muted mt-0.5">
                            Gerçekleşen: {formatCurrency(stats.totalActualCost)} / Tahmini: {formatCurrency(stats.totalEstimatedCost)}
                        </p>
                    </div>
                    <div className="flex items-center gap-4 text-xs font-medium">
                        <span className="flex items-center gap-1 text-success"><CheckCircle2 size={12} /> Tamamlanan: {stats.completedItems}</span>
                        <span className="flex items-center gap-1 text-warning"><Clock size={12} /> Devam Eden: {stats.inProgressItems}</span>
                        <span className="flex items-center gap-1 text-text-muted"><AlertCircle size={12} /> Bekleyen: {stats.pendingItems}</span>
                    </div>
                </div>
                <div className="h-3 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                    <div className={clsx("h-full rounded-full transition-all duration-700",
                        budgetProgress > 90 ? 'bg-error' : budgetProgress > 70 ? 'bg-warning' : 'bg-success'
                    )} style={{ width: `${budgetProgress}%` }} />
                </div>
                <div className="flex justify-between mt-1.5 text-[11px] text-text-muted">
                    <span>%{budgetProgress.toFixed(1)} kullanıldı</span>
                    <span>Kalan: {formatCurrency(stats.totalEstimatedCost - stats.totalActualCost)}</span>
                </div>
            </Card>

            {/* Malzeme Listesi - Kategori bazlı */}
            {categoryFilter === 'all' ? (
                Object.entries(groupedMaterials).map(([category, categoryMaterials]) => (
                    <div key={category} className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full bg-${MATERIAL_CATEGORIES[category]?.color || 'neutral'}`} />
                                <h2 className="text-lg font-semibold text-text-primary">
                                    {MATERIAL_CATEGORIES[category]?.label || category}
                                </h2>
                                <span className="text-sm text-text-tertiary">({categoryMaterials.length} kalem)</span>
                            </div>
                            <span className="text-sm font-semibold text-text-secondary">
                                {formatCompact(categoryMaterials.reduce((s, m) => s + calculateEstimatedCost(m), 0))}
                            </span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {categoryMaterials.map((material) => (
                                <MalzemeKarti key={material.id} material={material} onEdit={() => handleEdit(material)} onDelete={() => handleDelete(material)} formatCurrency={formatCurrency} />
                            ))}
                        </div>
                    </div>
                ))
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filteredMaterials.map((material) => (
                        <MalzemeKarti key={material.id} material={material} onEdit={() => handleEdit(material)} onDelete={() => handleDelete(material)} formatCurrency={formatCurrency} />
                    ))}
                </div>
            )}

            {/* Boş durum */}
            {filteredMaterials.length === 0 && (
                <div className="text-center py-12 text-text-tertiary">
                    <Package size={48} className="mx-auto mb-4 opacity-50" />
                    <p>Bu kategoride malzeme bulunamadı.</p>
                    <Button variant="primary" icon={<Plus size={18} />} onClick={handleAdd} className="mt-4">İlk Malzemeyi Ekle</Button>
                </div>
            )}

            {/* Modaller */}
            <MalzemeModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleSubmit} material={selectedMaterial} />
            <ConfirmModal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={confirmDelete}
                title="Malzemeyi Sil" message={`"${selectedMaterial?.name}" malzemesini silmek istediğinize emin misiniz?`}
                confirmText="Evet, Sil" variant="danger" />
            <MaliyetOzeti isOpen={showSummary} onClose={() => setShowSummary(false)} materials={materials} stats={stats} formatCurrency={formatCurrency} />
        </div>
    );
}
